/**
 * Series page displaying a series with its ordered essay list.
 * 
 * Features:
 * - Series title and description
 * - Ordered list of essays
 * - Link to each essay
 * - Status badges
 * - Previous/next navigation between essays
 * 
 * Route: /series/:slug
 * 
 * Called by:
 * - Navigation links
 * - SeriesManager component links
 */
import { useParams, Link } from 'react-router-dom';
import { Typography, Card, Space, Empty, Spin, List, Badge } from 'antd';
import {
  OrderedListOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { getSeriesWithEssays } from '../services/api';
import StatusBadge from '../components/StatusBadge';

const { Title, Text, Paragraph } = Typography;

// =============================================================================
// Main Component
// =============================================================================

/**
 * Series page component.
 * Displays a series with its essays in order.
 */
function SeriesPage() {
  const { slug } = useParams<{ slug: string }>();

  // Fetch series with essays
  const { data: series, isLoading, isError, error } = useQuery({
    queryKey: ['series', slug],
    queryFn: () => getSeriesWithEssays(slug!),
    enabled: !!slug,
  });

  // Loading state
  if (isLoading) {
    return (
      <div style={{ padding: '48px 0', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div style={{ padding: '48px 0' }}>
        <Empty
          description={
            error instanceof Error ? error.message : 'Failed to load series'
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // Not found
  if (!series) {
    return (
      <div style={{ padding: '48px 0' }}>
        <Empty
          description="Series not found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 0' }}>
      {/* Series header */}
      <div style={{ marginBottom: 32 }}>
        <Space align="center" style={{ marginBottom: 8 }}>
          <OrderedListOutlined style={{ fontSize: 24, color: '#d4af37' }} />
          <Title level={2} style={{ margin: 0 }}>
            {series.name}
          </Title>
        </Space>

        {series.description && (
          <Paragraph
            type="secondary"
            style={{ fontSize: 16, maxWidth: 600, marginBottom: 0 }}
          >
            {series.description}
          </Paragraph>
        )}

        <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
          {series.essays.length} essay{series.essays.length !== 1 ? 's' : ''} in this series
        </Text>
      </div>

      {/* Essays list */}
      {series.essays.length === 0 ? (
        <Empty
          description="No essays in this series yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <List
          dataSource={series.essays}
          renderItem={(item) => (
            <List.Item style={{ padding: 0, marginBottom: 16 }}>
              <Card
                style={{
                  width: '100%',
                  background: '#1a1a1a',
                  border: '1px solid #333',
                }}
                hoverable
              >
                <Link to={`/reviewer/essays/${item.essay.slug}`}>
                  <Space align="start" style={{ width: '100%' }}>
                    {/* Part number badge */}
                    <Badge
                      count={item.orderIndex + 1}
                      style={{
                        background: '#d4af37',
                        marginRight: 8,
                        minWidth: 28,
                        height: 28,
                        lineHeight: '28px',
                        fontSize: 14,
                      }}
                    />

                    {/* Essay info */}
                    <div style={{ flex: 1 }}>
                      <Title level={4} style={{ marginBottom: 8 }}>
                        {item.essay.title}
                      </Title>

                      {item.essay.preview && (
                        <Paragraph
                          type="secondary"
                          style={{ marginBottom: 8, fontSize: 14 }}
                          ellipsis={{ rows: 2 }}
                        >
                          {item.essay.preview}
                        </Paragraph>
                      )}

                      <Space>
                        <StatusBadge status={item.essay.status} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Updated {new Date(item.essay.updatedAt).toLocaleDateString()}
                        </Text>
                      </Space>
                    </div>
                  </Space>
                </Link>
              </Card>
            </List.Item>
          )}
        />
      )}
    </div>
  );
}

export default SeriesPage;
